import { customElement, FASTElement, observable, Observable, Splice } from '@microsoft/fast-element';
import { AnalyticsStepOneTemplate as template } from './analytics-step-one.template';
import { AnalyticsStepOneStyles as styles } from './analytics-step-one.styles';
import { SegmentedControl, Select } from '@genesislcap/foundation-zero';
import { IBuyerAnalyticsService, ICommonService } from '../../../../services';
import { AnalyticsPreferences, IHubZoneMatrix } from '../../../../types';
import { QuadrantGrid } from './quadrant-grid/quadrant-grid';
import { PRODUCT_TYPE } from '../../../../utils';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { ICompanyService } from '../../../../services/company.service';

const name = 'analytics-step-one';

type Options = "0" | "1" | "2" | "3" | "4"

export enum PREFERENCES_PRODUCT_TYPE {
  VPPA = 'VPPA',
  REC = 'REC',
}

@customElement({
  name,
  template,
  styles,
})
export class AnalyticsStepOne extends FASTElement {
  @ICommonService commonService: ICommonService;
  @IBuyerAnalyticsService buyerAnalyticsService: IBuyerAnalyticsService;
  @IEnumService enumService: IEnumService;
  @ICompanyService private companyService: ICompanyService;

  private _hubZoneMatrix: Array<IHubZoneMatrix> = [];

  @observable locationDDElement: Select;
  @observable productTypeValue: PRODUCT_TYPE;
  @observable productTypeElement: Select;
  @observable applicableRECProgramElement: Select;
  @observable productTypes: Array<EnumTranslate>;
  @observable applicableRECProgramTypes: Array<EnumTranslate>;
  @observable applicableRECProgramValue: string;
  @observable locationDDValue: string;
  @observable iso = [];
  @observable location: Options = '0';
  @observable expectedSettlement: Options = '0';
  @observable nodalLme: Options = '0';
  @observable impliedCarbonPrice: Options = '0';
  @observable additionality: Options = '0';
  @observable quadrantGrid: QuadrantGrid;
  @observable disabledConfirmButton: boolean = true;
  @observable currentPreferences: AnalyticsPreferences;

  @observable expectedSettlementElement: SegmentedControl;
  @observable additionalityElement: SegmentedControl;

  private currentPreferencesNotifier: any;
  private currentPreferencesHandler: any;

  constructor() {
    super();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.productTypeValue = this.companyService.isNonSefParticipantAgreement()
      ? PRODUCT_TYPE.PHYSICAL_PPA
      : PRODUCT_TYPE.VIRTUAL_PPA;
    this.loadEnums();
    this.getAllIso();
    this.isConfirmDisabled()
    this.loadCurrentPreferences()
    /*
       There is no render onRenderedCallback options or microsoft-fast
       And a no 'render' element event for this to make it possible to
       load data after readering setTimeout with 0 called to apply post
       rendered events.
    */
    setTimeout(() => {
        this.setPreferences()
        },
        0
    )
  }


  private setPreferences() {
      if (this.buyerAnalyticsService.currentPreferences) {
                this.locationDDValue = this.buyerAnalyticsService.currentPreferences.PREFERRED_LOCATION
                this.productTypeValue = this.buyerAnalyticsService.currentPreferences.PRODUCT_TYPE as PRODUCT_TYPE;
                this.location = this.buyerAnalyticsService.currentPreferences.LOCATION as Options;
                this.expectedSettlement = this.buyerAnalyticsService.currentPreferences.EXPECTED_SETTLEMENT as Options;
                this.nodalLme = this.buyerAnalyticsService.currentPreferences.LME as Options;
                this.impliedCarbonPrice = this.buyerAnalyticsService.currentPreferences.IMPLIED_CARBON_PRICE as Options;
                this.additionality = this.buyerAnalyticsService.currentPreferences.PROJECT_ADDITIONALITY as Options;
                this.locationDDElement.value = this.locationDDValue;
                this.productTypeElement.value = this.productTypeValue;
                this.applicableRECProgramValue = this.buyerAnalyticsService.currentPreferences.APPLICABLE_REC_PROGRAM ?? 'ALL';
                this.applicableRECProgramElement.value = this.applicableRECProgramValue;
      }
  }


  private loadCurrentPreferences() {
    const context = this;
    this.currentPreferencesNotifier = Observable.getNotifier(this.buyerAnalyticsService);
    this.currentPreferencesHandler = {
      handleChange(source: any, splices: Splice[]) {
        if (source.currentPreferences){
            context.currentPreferences = source.currentPreferences;
        } else {
            context.resetPreferences()
        }
      },
    };

    this.currentPreferencesNotifier.subscribe(this.currentPreferencesHandler, 'currentPreferences');
  }

  public currentPreferencesChanged(oldValue, newValue?: AnalyticsPreferences) {
    if (!newValue) {
        this.resetPreferences()
    }
  }

  private async loadEnums() {
    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.PRODUCT_TYPE).then((response) => {
      if (response.REPLY) {
        this.productTypes = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM).then((response) => {
        if (response.REPLY) {
            this.applicableRECProgramTypes = response.REPLY;
            if (!this.applicableRECProgramTypes.find(program => program.ENUM_VALUE === "ALL")) {
              this.applicableRECProgramTypes.push({ "ENUM_VALUE": "ALL", "READABLE_VALUE": "All" });
            }
            this.applicableRECProgramValue = 'ALL';
        }
    });
  }

  public productTypeValueChanged() {
    if (this.productTypeValue === PRODUCT_TYPE.RECS) {
      this.expectedSettlement = '0';
      this.additionality = '0';
      this.expectedSettlementElement.setAttribute('disabled', 'true');
      this.additionalityElement.setAttribute('disabled', 'true');
    } else {
      this.expectedSettlementElement?.setAttribute('disabled', 'false');
      this.additionalityElement?.setAttribute('disabled', 'false');
    }
  }

  public resetPreferences() {
    if (this.iso.length) {
      this.locationDDValue = this.iso[0];
    }

    this.productTypeValue = PRODUCT_TYPE.VIRTUAL_PPA;
    this.applicableRECProgramValue = 'ALL';
    this.location = '0';
    this.expectedSettlement = '0';
    this.nodalLme = '0';
    this.impliedCarbonPrice = '0';
    this.additionality = '0';
    this.buyerAnalyticsService.selectedVppas = []
    this.buyerAnalyticsService.vppaRankings = []
    this.quadrantGrid.enableProceedButton = false;
    sessionStorage.removeItem('buyerSelectedVppas')
    sessionStorage.removeItem('buyerVppaRankings')
    this.buyerAnalyticsService.currentPreferences = this.getFormValues()
    setTimeout(() => {
        this.setPreferences()
        this.$emit('enableProceedButton', false)
        },
        0
    )
  }
  private getAllIso() {
    this.commonService.getHubZoneMatrix().then((response) => {
      if (response.REPLY) {
        this._hubZoneMatrix = response.REPLY;
        this.generateIsoDD();
      }
    });
  }

  private generateIsoDD() {
    this.iso = [...new Set(this._hubZoneMatrix.map((item) => item.ISO))];
    if (this.buyerAnalyticsService.currentPreferences) {
        if (this.buyerAnalyticsService.currentPreferences.PREFERRED_LOCATION) {
            this.locationDDValue = this.buyerAnalyticsService.currentPreferences.PREFERRED_LOCATION
        } else {
            this.locationDDValue = this.iso[0]
        };
    }
    else {
        this.locationDDValue = this.iso[0];
    }
  }

  locationChanged(oldValue, newValue) {
    if (oldValue) this.isConfirmDisabled();
  }

  expectedSettlementChanged(oldValue, newValue) {
    if (oldValue) this.isConfirmDisabled();
  }

  nodalLmeChanged(oldValue, newValue) {
    if (oldValue) this.isConfirmDisabled();
  }

  impliedCarbonPriceChanged(oldValue, newValue) {
    if (oldValue) this.isConfirmDisabled();
  }

  additionalityChanged(oldValue, newValue) {
    if (oldValue) this.isConfirmDisabled();
  }

  public isConfirmDisabled() {
    if (
      this.location === '0' &&
      this.expectedSettlement == '0' &&
      this.nodalLme == '0' &&
      this.impliedCarbonPrice == '0' &&
      this.additionality == '0'
    ) {
      this.disabledConfirmButton = true;
    } else {
      this.disabledConfirmButton = false;
    }
  }

  public confirm() {
    const preferences: AnalyticsPreferences = this.getFormValues();
    this.buyerAnalyticsService.productType = this.productTypeValue;
    this.buyerAnalyticsService.setVppaRankings(preferences);
  }

  public confirmReset() {
    this.confirm();
    this.buyerAnalyticsService.selectedVppas = [];
    sessionStorage.removeItem('buyerSelectedVppas')
    sessionStorage.removeItem('buyerVppaRankings')
    this.quadrantGrid.enableProceedButton = false;
  }

  public getFormValues(): AnalyticsPreferences {
    const formValues: AnalyticsPreferences = {
      PRODUCT_TYPE: this.productTypeValue,
      PREFERRED_LOCATION: this.locationDDValue,
      LOCATION: this.location,
      EXPECTED_SETTLEMENT: this.expectedSettlement,
      LME: this.nodalLme,
      IMPLIED_CARBON_PRICE: this.impliedCarbonPrice,
      PROJECT_ADDITIONALITY: this.additionality,
    };

    if (this.applicableRECProgramValue !== 'ALL') {
      formValues.APPLICABLE_REC_PROGRAM = this.applicableRECProgramValue;
    }
    return formValues;
  }

  public proceedToDrilldown(event) {
    this.quadrantGrid.enableProceedButton = event;
  }
}

